import React from 'react'
import { Button, Flex, H2, Input, Span, SelectDropdown, Line } from '../../lib/components'
import { Pagination } from '../../lib/components/Pagination'
import { useBreakpoint } from '../../lib/utils/hooks'
import { SM } from '../../lib/utils/constants'
import { GetBlogsSearchParams } from '../../lib/types'
import { blogSort } from '../../lib/utils/message'
import { BlogCard } from './blog-card'
import './blog-list.scss'
import { BLOG_CATEGORIES, BLOG_STATUSES } from '../blogs-form/constants'

type BlogListPresenterProps = {
  blogs: any[]
  searchParams: GetBlogsSearchParams
  handleSearchParams: (params: { name: string; value: string }) => void
  resetSearchParams: () => void
  pagination: {
    total: number
    limit: number
    count: number
    offset: number
  }
  handleCurrentPage: (currentPage: number) => void
  currentPage: number
  handleBlogEdit: (id: number) => void
}

function BlogListPresenter({
  blogs,
  searchParams,
  handleSearchParams,
  resetSearchParams,
  pagination,
  handleCurrentPage,
  currentPage,
  handleBlogEdit,
}: BlogListPresenterProps) {
  const { breakpoint } = useBreakpoint()

  return (
    <Flex variant='column-center'>
      {/* Heading */}
      <H2 classes={['mb-48']} text='Browse Blogs' />

      {/* Filters. */}
      <div className='blog-list-toolbar-layout gap-12 mb-16 mt-16'>
        <Input
          name='keyword'
          value={searchParams.keyword || ''}
          handleChange={(e) => handleSearchParams({ name: e.target.name, value: e.target.value })}
          placeholder='Search'
        />
        <div className='sort-reset'>
          <SelectDropdown
            name='sortBy'
            id='sortById'
            formId=''
            list={blogSort}
            placeholder={'Sort'}
            value={searchParams.sortBy}
            handleChange={(val) => handleSearchParams({ name: 'sortBy', value: val })}
            dataTestId='blog-list-sort-select'
          />
          <SelectDropdown
            name='status'
            id='statusId'
            formId=''
            list={BLOG_STATUSES as any}
            placeholder={'Status'}
            value={searchParams.status || ''}
            handleChange={(val: string) => handleSearchParams({ name: 'status', value: val })}
            dataTestId='blog-list-status-select'
          />
          <SelectDropdown
            name='category'
            id='categoryId'
            formId=''
            list={BLOG_CATEGORIES as any}
            placeholder={'Category'}
            value={searchParams.category || ''}
            handleChange={(val: string) => handleSearchParams({ name: 'category', value: val })}
            dataTestId='blog-list-category-select'
          />
          <Button
            handleClick={() => resetSearchParams()}
            classes={['reset-btn']}
            variant='secondary-btn'
            text='Reset'
          />
        </div>
      </div>

      {/* Table. */}
      <Line />
      {blogs && blogs.length > 0 ? (
        <>
          <div className='w-100'>
            <Flex
              variant='row'
              classes={['mb-24', 'table_scroll_horizontal']}
              dataTestId='blogs-table-container'
            >
              <table className='styled-table'>
                <thead>
                  <tr>
                    <th>Banner Image</th>
                    <th>Status</th>
                    <th>Title</th>
                    <th>Subtitle</th>
                    <th>Content</th>
                    <th>Category</th>
                    <th>Slug</th>
                    <th>LinkedIn URL</th>
                    <th>Created At</th>
                    <th>Updated At</th>
                    <th>Edit</th>
                  </tr>
                </thead>
                <tbody>
                  {blogs.map(
                    ({
                      id,
                      banner_image,
                      title,
                      sub_title,
                      content,
                      status,
                      category,
                      slug,
                      linkedin_url,
                      created_at,
                      updated_at,
                    }: any) => (
                      <BlogCard
                        key={id}
                        id={id}
                        bannerImage={banner_image}
                        title={title}
                        subtitle={sub_title}
                        content={content}
                        slug={slug}
                        linkedinUrl={linkedin_url}
                        status={status}
                        category={category}
                        createdAt={created_at}
                        updatedAt={updated_at}
                        handleEditClick={handleBlogEdit}
                      />
                    ),
                  )}
                </tbody>
              </table>
            </Flex>
          </div>
          {breakpoint && breakpoint === SM ? null : (
            <Pagination
              currentPage={currentPage}
              handleCurrentPage={handleCurrentPage}
              pagination={pagination}
            />
          )}
        </>
      ) : (
        <Flex variant={'column-center'} classes={['mt-12']}>
          <Span classes={['center']} variant={'thin'} text='No Blogs Found' />
        </Flex>
      )}
    </Flex>
  )
}

export { BlogListPresenter }
